import React, { useCallback, useEffect } from 'react'
import { Input, Table, Button, DatePicker, Select, Space, Card, Tag, Modal } from 'antd'
import { SearchOutlined, ReloadOutlined, EyeOutlined, RedoOutlined } from '@ant-design/icons'
import styled from '@emotion/styled'
import api from '../../api'
import moment from 'moment'
import { useLocation, useHistory } from 'react-router-dom'
import { createSimpleStore } from 'react-simple-reducer'
import { CopyButton } from '../../components/CopyButton'

const { RangePicker } = DatePicker
const { Option } = Select

interface DetranLog {
  id: number
  state: string
  operation: string
  matriculaId: number
  usuarioId: number
  requestBody: string
  responseBody: string
  responseType: 'SUCCESS' | 'ERROR' | 'TIMEOUT' | 'NETWORK_ERROR'
  createdAt: string
  updatedAt: string
  method: string
  endpoint: string
  matricula?: {
    id: number
    aluno?: {
      nome: string
      cpf: string
    }
    curso?: {
      nomeReduzido: string
    }
  }
}

interface SearchParams {
  cpf: string
  matriculaId: string
  state: string
  operation: string
  dateFrom: string
  dateTo: string
  limit: number
  offset: number
}

interface DetranLogsState {
  logs: DetranLog[]
  total: number
  loading: boolean
  error: string | null
  searchParams: SearchParams
  selectedLog: DetranLog | null
  modalVisible: boolean
}

const defaultSearchParams = {
  cpf: '',
  matriculaId: '',
  state: '',
  operation: '',
  dateFrom: '',
  dateTo: '',
  limit: 15,
  offset: 0,
}

const DetranLogsStore = createSimpleStore(
  {
    logs: [],
    total: 0,
    loading: false,
    error: null,
    selectedLog: null,
    modalVisible: false,
    searchParams: defaultSearchParams,
  } as DetranLogsState,
  {
    setLoading: (state, loading: boolean) => ({
      ...state,
      loading,
    }),
    setError: (state, error: string | null) => ({
      ...state,
      error,
    }),
    setLogs: (state, { logs, total }: { logs: DetranLog[]; total: number }) => ({
      ...state,
      logs,
      total,
    }),
    updateSearchParam: (state, { field, value }: { field: keyof SearchParams; value: any }) => ({
      ...state,
      searchParams: {
        ...state.searchParams,
        [field]: value,
      },
    }),
    resetSearch: (state) => ({
      ...state,
      searchParams: {
        cpf: '',
        matriculaId: '',
        state: '',
        operation: '',
        dateFrom: '',
        dateTo: '',
        limit: 50,
        offset: 0,
      },
    }),
    updatePagination: (state, { offset, limit }: { offset: number; limit: number }) => ({
      ...state,
      searchParams: {
        ...state.searchParams,
        offset,
        limit,
      },
    }),
    openLogModal: (state, log: DetranLog) => ({
      ...state,
      selectedLog: log,
      modalVisible: true,
    }),
    closeLogModal: (state) => ({
      ...state,
      selectedLog: null,
      modalVisible: false,
    }),
  },
  {
    thunks: {
      fetchLogs(searchParams?: SearchParams) {
        return async (dispatch) => {
          dispatch(DetranLogsStore.actions.setLoading(true))
          dispatch(DetranLogsStore.actions.setError(null))

          try {
            const response = await api.DetranLogs.get(searchParams)

            dispatch(
              DetranLogsStore.actions.setLogs({
                logs: response.logs,
                total: response.total,
              })
            )
          } catch (error: any) {
            dispatch(DetranLogsStore.actions.setError(error.message || 'Erro ao carregar logs'))
          } finally {
            dispatch(DetranLogsStore.actions.setLoading(false))
          }
        }
      },
    },
  }
)

export const DetranLogs = () => {
  const location = useLocation()
  const history = useHistory()

  const initFn = useCallback(
    (dispatch: ReturnType<typeof DetranLogsStore.useDispatch>) => {
      const urlParams = new URLSearchParams(location.search)
      const matriculaId = urlParams.get('matriculaId')

      if (matriculaId) {
        dispatch(
          DetranLogsStore.actions.updateSearchParam({ field: 'matriculaId', value: matriculaId })
        )
        dispatch(DetranLogsStore.thunks.fetchLogs({ ...defaultSearchParams, matriculaId }))
      } else {
        dispatch(DetranLogsStore.thunks.fetchLogs())
      }
    },
    [location.search]
  )

  return (
    <DetranLogsStore.Provider init={initFn}>
      <Container>
        <PageHeader>
          <Title>Logs DETRAN</Title>
        </PageHeader>

        <SearchSection />
        <ResultsSection />
        <DetailsModal />

        <DetranLogsStore.GetState>
          {(state) => state.error && <ErrorMessage>{state.error}</ErrorMessage>}
        </DetranLogsStore.GetState>
      </Container>
    </DetranLogsStore.Provider>
  )
}

const SearchSection = () => {
  const { searchParams, loading } = DetranLogsStore.useState()
  const dispatch = DetranLogsStore.useDispatch()

  const handleSearch = () => {
    dispatch(DetranLogsStore.thunks.fetchLogs(searchParams))
  }

  const handleReset = () => {
    dispatch(DetranLogsStore.actions.resetSearch())
  }

  const handleDateChange = (dates: any) => {
    if (dates && dates.length === 2) {
      dispatch(
        DetranLogsStore.actions.updateSearchParam({
          field: 'dateFrom',
          value: dates[0].format('YYYY-MM-DD'),
        })
      )
      dispatch(
        DetranLogsStore.actions.updateSearchParam({
          field: 'dateTo',
          value: dates[1].format('YYYY-MM-DD'),
        })
      )
    } else {
      dispatch(DetranLogsStore.actions.updateSearchParam({ field: 'dateFrom', value: '' }))
      dispatch(DetranLogsStore.actions.updateSearchParam({ field: 'dateTo', value: '' }))
    }
  }

  return (
    <SearchCard>
      <SearchGrid>
        <SearchItem>
          <Label>CPF</Label>
          <Input
            placeholder="Buscar por CPF no request/response"
            value={searchParams.cpf}
            onChange={(e) =>
              dispatch(
                DetranLogsStore.actions.updateSearchParam({
                  field: 'cpf',
                  value: e.target.value,
                })
              )
            }
          />
        </SearchItem>

        <SearchItem>
          <Label>ID Matrícula</Label>
          <Input
            placeholder="ID da matrícula"
            value={searchParams.matriculaId}
            onChange={(e) =>
              dispatch(
                DetranLogsStore.actions.updateSearchParam({
                  field: 'matriculaId',
                  value: e.target.value,
                })
              )
            }
          />
        </SearchItem>

        <SearchItem>
          <Label>Estado</Label>
          <Select
            placeholder="Selecione o estado"
            value={searchParams.state || undefined}
            onChange={(value) =>
              dispatch(
                DetranLogsStore.actions.updateSearchParam({
                  field: 'state',
                  value: value || '',
                })
              )
            }
            showSearch
            allowClear
            style={{ width: '100%' }}
          >
            <Option value="AC">AC</Option>
            <Option value="AL">AL</Option>
            <Option value="AP">AP</Option>
            <Option value="AM">AM</Option>
            <Option value="BA">BA</Option>
            <Option value="CE">CE</Option>
            <Option value="DF">DF</Option>
            <Option value="ES">ES</Option>
            <Option value="GO">GO</Option>
            <Option value="MA">MA</Option>
            <Option value="MT">MT</Option>
            <Option value="MS">MS</Option>
            <Option value="MG">MG</Option>
            <Option value="PA">PA</Option>
            <Option value="PB">PB</Option>
            <Option value="PR">PR</Option>
            <Option value="PE">PE</Option>
            <Option value="PI">PI</Option>
            <Option value="RJ">RJ</Option>
            <Option value="RN">RN</Option>
            <Option value="RS">RS</Option>
            <Option value="RO">RO</Option>
            <Option value="RR">RR</Option>
            <Option value="SC">SC</Option>
            <Option value="SP">SP</Option>
            <Option value="SE">SE</Option>
            <Option value="TO">TO</Option>
          </Select>
        </SearchItem>

        <SearchItem>
          <Label>Operação</Label>
          <Select
            placeholder="Selecione a operação"
            value={searchParams.operation || undefined}
            onChange={(value) =>
              dispatch(
                DetranLogsStore.actions.updateSearchParam({
                  field: 'operation',
                  value: value || '',
                })
              )
            }
            allowClear
            style={{ width: '100%' }}
          >
            <Option value="validar">Validar</Option>
            <Option value="matricular">Matricular</Option>
            <Option value="concluir">Concluir</Option>
            <Option value="cancelar_matricula">Cancelar Matrícula</Option>
            <Option value="cancelar_conclusao">Cancelar Conclusão</Option>
            <Option value="atualizar">Atualizar</Option>
          </Select>
        </SearchItem>

        <SearchItem>
          <Label>Período</Label>
          <RangePicker format="DD/MM/YYYY" onChange={handleDateChange} style={{ width: '100%' }} />
        </SearchItem>
      </SearchGrid>

      <SearchActions>
        <Button type="primary" icon={<SearchOutlined />} onClick={handleSearch} loading={loading}>
          Buscar
        </Button>
        <Button icon={<ReloadOutlined />} onClick={handleReset}>
          Limpar
        </Button>
      </SearchActions>
    </SearchCard>
  )
}

const ResultsSection = () => {
  const { logs, total, loading, searchParams } = DetranLogsStore.useState()
  const dispatch = DetranLogsStore.useDispatch()
  const history = useHistory()

  const getStatusColor = (responseType: string) => {
    switch (responseType) {
      case 'SUCCESS':
        return 'green'
      case 'ERROR':
        return 'red'
      case 'TIMEOUT':
        return 'orange'
      case 'NETWORK_ERROR':
        return 'purple'
      default:
        return 'default'
    }
  }

  const viewDetails = (log: DetranLog) => {
    dispatch(DetranLogsStore.actions.openLogModal(log))
  }

  const retry = (log: DetranLog) => {
    history.push(`/detran-actions?matriculaId=${log.matriculaId}`)
  }

  const handlePaginationChange = async (page: number, pageSize?: number) => {
    dispatch(
      DetranLogsStore.actions.updatePagination({
        offset: (page - 1) * (pageSize || searchParams.limit),
        limit: pageSize || searchParams.limit,
      })
    )
    await dispatch(DetranLogsStore.thunks.fetchLogs())
  }

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: 'Data/Hora',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 150,
      render: (text: string) => moment(text).format('DD/MM/YYYY HH:mm:ss'),
    },
    {
      title: 'Estado',
      dataIndex: 'state',
      key: 'state',
      width: 80,
    },
    {
      title: 'Operação',
      dataIndex: 'operation',
      key: 'operation',
      width: 120,
    },
    {
      title: 'Status',
      dataIndex: 'responseType',
      key: 'responseType',
      width: 120,
      render: (responseType: string) => (
        <Tag color={getStatusColor(responseType)}>{responseType}</Tag>
      ),
    },
    {
      title: 'Estudante',
      key: 'student',
      width: 200,
      render: (record: DetranLog) => (
        <div>
          <div>
            <strong>{record.matricula?.aluno?.nome || 'N/A'}</strong>
          </div>
          <div
            style={{
              fontSize: '12px',
              color: '#666',
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
            }}
          >
            <span>CPF: {record.matricula?.aluno?.cpf || 'N/A'}</span>
            {record.matricula?.aluno?.cpf && (
              <CopyButton value={record.matricula.aluno.cpf} size="small" />
            )}
          </div>
        </div>
      ),
    },
    {
      title: 'Matrícula ID',
      dataIndex: 'matriculaId',
      key: 'matriculaId',
      width: 100,
    },
    {
      title: 'Curso',
      key: 'course',
      width: 150,
      render: (record: DetranLog) => record.matricula?.curso?.nomeReduzido || 'N/A',
    },
    {
      title: 'Ações',
      key: 'actions',
      width: 120,
      render: (record: DetranLog) => (
        <Space>
          <Button size="small" icon={<EyeOutlined />} onClick={() => viewDetails(record)}>
            Ver
          </Button>
          {record.responseType === 'ERROR' && (
            <Button
              size="small"
              type="primary"
              icon={<RedoOutlined />}
              onClick={() => retry(record)}
            >
              Repetir
            </Button>
          )}
        </Space>
      ),
    },
  ]

  return (
    <ResultsCard>
      <Table
        columns={columns}
        dataSource={logs}
        loading={loading}
        rowKey="id"
        pagination={{
          current: Math.floor(searchParams.offset / searchParams.limit) + 1,
          pageSize: searchParams.limit,
          total: total,
          onChange: handlePaginationChange,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total, range) => `${range[0]}-${range[1]} de ${total} registros`,
        }}
        scroll={{ x: 1024 }}
      />
    </ResultsCard>
  )
}

const DetailsModal = () => {
  const { selectedLog, modalVisible } = DetranLogsStore.useState()
  const dispatch = DetranLogsStore.useDispatch()
  const history = useHistory()

  const handleClose = () => {
    dispatch(DetranLogsStore.actions.closeLogModal())
  }

  const formatJson = (jsonString: string): string => {
    const formatXml = (xml: string): string => {
      // First, replace escaped newlines
      xml = xml.replace(/\\n/g, '\n')

      // If XML already has line breaks, return as is
      if (xml.includes('\n')) {
        return xml
      }

      // Format one-lined XML
      const PADDING = '  '
      let formatted = ''
      let pad = 0

      xml.split(/>\s*</).forEach((node) => {
        if (node.match(/^\/\w/)) {
          // Closing tag
          pad -= 1
          formatted += PADDING.repeat(pad) + '</' + node.substring(1) + '>\n'
        } else if (node.match(/^<?\w[^>]*[^\/]$/)) {
          // Opening tag
          formatted += PADDING.repeat(pad) + '<' + node + '>\n'
          pad += 1
        } else {
          // Self-closing tag or text content
          formatted += PADDING.repeat(pad) + '<' + node + '>\n'
        }
      })

      // Clean up the formatting
      return formatted
        .replace(/(<(\w+)[^>]*>)\s*(<\/\2>)/g, '$1$3') // Remove whitespace between empty tags
        .replace(/\n\s*\n/g, '\n') // Remove empty lines
        .trim()
    }

    const recursivelyParseJson = (obj: any): any => {
      if (typeof obj === 'string') {
        // Check if it looks like XML
        if (obj.trim().startsWith('<') && obj.trim().endsWith('>')) {
          return formatXml(obj)
        }

        // Check if it looks like JSON
        if (
          (obj.startsWith('{') && obj.endsWith('}')) ||
          (obj.startsWith('[') && obj.endsWith(']'))
        ) {
          try {
            const parsed = JSON.parse(obj)
            return recursivelyParseJson(parsed)
          } catch {
            // If it's not valid JSON, check if it contains newlines
            if (obj.includes('\\n')) {
              return obj.replace(/\\n/g, '\n')
            }
            return obj
          }
        }

        // Handle strings with escaped newlines
        if (obj.includes('\\n')) {
          return obj.replace(/\\n/g, '\n')
        }
        return obj
      }

      if (Array.isArray(obj)) {
        return obj.map((item) => recursivelyParseJson(item))
      }

      if (obj && typeof obj === 'object') {
        const result: any = {}
        for (const key in obj) {
          result[key] = recursivelyParseJson(obj[key])
        }
        return result
      }

      return obj
    }

    try {
      const parsed = typeof jsonString === 'string' ? JSON.parse(jsonString) : jsonString
      const deepParsed = recursivelyParseJson(parsed)
      return JSON.stringify(deepParsed, null, 2)
    } catch {
      // If initial parse fails, might be XML or plain text
      if (typeof jsonString === 'string') {
        // Check if it's XML
        if (jsonString.trim().startsWith('<') && jsonString.trim().endsWith('>')) {
          return formatXml(jsonString)
        }
        // Handle escaped newlines
        if (jsonString.includes('\\n')) {
          return jsonString.replace(/\\n/g, '\n')
        }
      }
      return jsonString
    }
  }

  function handleClickEnviarAcao() {
    history.push(`/detran-actions?matriculaId=${selectedLog.matriculaId}`)
  }

  return (
    <Modal
      title={`Detalhes do Log #${selectedLog?.id || ''}`}
      open={modalVisible}
      onCancel={handleClose}
      footer={[
        <Button key="close" onClick={handleClose}>
          Fechar
        </Button>,
      ]}
      width={800}
    >
      {selectedLog && (
        <DetailsContainer>
          <DetailSection>
            <SectionTitle>Informações Gerais</SectionTitle>
            <DetailGrid>
              <DetailItem>
                <DetailLabel>ID:</DetailLabel>
                <DetailValue>{selectedLog.id}</DetailValue>
              </DetailItem>
              <DetailItem>
                <DetailLabel>Estado:</DetailLabel>
                <DetailValue>{selectedLog.state}</DetailValue>
              </DetailItem>
              <DetailItem>
                <DetailLabel>Operação:</DetailLabel>
                <DetailValue>{selectedLog.operation}</DetailValue>
              </DetailItem>
              <DetailItem>
                <DetailLabel>Status:</DetailLabel>
                <Tag color={getStatusColor(selectedLog.responseType)}>
                  {selectedLog.responseType}
                </Tag>
              </DetailItem>
              <DetailItem>
                <DetailLabel>Data/Hora:</DetailLabel>
                <DetailValue>
                  {moment(selectedLog.createdAt).format('DD/MM/YYYY HH:mm:ss')}
                </DetailValue>
              </DetailItem>
              <DetailItem>
                <DetailLabel>Matrícula ID:</DetailLabel>
                <DetailValue>{selectedLog.matriculaId}</DetailValue>
              </DetailItem>
            </DetailGrid>
          </DetailSection>

          <DetailItem style={{ overflow: 'auto', whiteSpace: 'nowrap' }}>
            <DetailLabel>Endpoint:</DetailLabel>
            <DetailValue>
              [{selectedLog.method}] {selectedLog.endpoint}
            </DetailValue>
          </DetailItem>

          <Button onClick={handleClickEnviarAcao}>Enviar ação</Button>

          <DetailSection>
            <SectionTitle>Request</SectionTitle>
            <JsonContainer>
              <pre>{formatJson(selectedLog.requestBody)}</pre>
            </JsonContainer>
          </DetailSection>

          <DetailSection>
            <SectionTitle>Response</SectionTitle>
            <JsonContainer>
              <pre>{formatJson(selectedLog.responseBody)}</pre>
            </JsonContainer>
          </DetailSection>
        </DetailsContainer>
      )}
    </Modal>
  )

  function getStatusColor(responseType: string) {
    switch (responseType) {
      case 'SUCCESS':
        return 'green'
      case 'ERROR':
        return 'red'
      case 'TIMEOUT':
        return 'orange'
      case 'NETWORK_ERROR':
        return 'purple'
      default:
        return 'default'
    }
  }
}

const Container = styled.div`
  padding: 20px;
  background-color: whitesmoke;
  max-width: calc(100vw - 20px);

  @media (max-width: 768px) {
    padding: 12px;
  }
`

const PageHeader = styled.div`
  margin-bottom: 24px;
`

const Title = styled.span`
  font-size: 22px;
  color: #494d50;
  font-weight: 600;
`

const SearchCard = styled(Card)`
  margin-bottom: 24px;
  .ant-card-body {
    padding: 24px;
  }
`

const SearchGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
  margin-bottom: 16px;
`

const SearchItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const Label = styled.label`
  font-weight: 500;
  color: #333;
`

const SearchActions = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
`

const ResultsCard = styled(Card)`
  .ant-card-body {
    padding: 0;
  }
`

const ErrorMessage = styled.div`
  background: #ff4d4f;
  color: white;
  padding: 12px;
  border-radius: 4px;
  margin-top: 16px;
`

const DetailsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`

const DetailSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`

const SectionTitle = styled.h4`
  font-weight: 600;
  color: #333;
  margin: 0;
`

const DetailGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 12px;
`

const DetailItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const DetailLabel = styled.span`
  font-weight: 500;
  color: #666;
  font-size: 12px;
`

const DetailValue = styled.span`
  color: #333;
  font-size: 14px;
`

const JsonContainer = styled.div`
  background: #f5f5f5;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  padding: 12px;
  overflow: auto;
  max-height: 400px;

  pre {
    margin: 0;
    font-size: 12px;
    line-height: 1.5;
    font-family: 'Consolas', 'Monaco', 'Courier New', monospace;
    white-space: pre-wrap;
    word-break: break-word;
  }
`
